/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import React, { ErrorInfo } from "react";
import { SeverityLevel } from "@microsoft/applicationinsights-web";
import appInsights from "../../AppInsights/appInsightsTelemetryClient";
import { CustomProperties } from "../../AppInsights/ITelemetryClient";
import HttpRequestError from "../../Services/HttpRequestError";
import { useAppSnackbar } from "../AppSnackbarContext";
import IAppSnackbar from "../AppSnackbarContext/IAppSnackbar";
import ErrorView from "./ErrorView";

export type GlobalErrorHandlerProps = {
    snackbar: IAppSnackbar;
    children: React.ReactNode;
}

type GlobalErrorHandlerType = React.ComponentType<GlobalErrorHandlerProps>;

const withSnackbar = (GlobalErrorHandler: GlobalErrorHandlerType) => {
    return (props: Omit<GlobalErrorHandlerProps, "snackbar">) => {
        const snackbar = useAppSnackbar();
        return <GlobalErrorHandler snackbar={snackbar} {...props} />
    }
}

export type GlobalErrorHandlerState = {
    hasComponentError: boolean;
};

class GlobalErrorHandler extends React.Component<GlobalErrorHandlerProps, GlobalErrorHandlerState> {
    public state: GlobalErrorHandlerState = {
        hasComponentError: false,
    };

    public static getDerivedStateFromError(_: Error): GlobalErrorHandlerState {
        return { hasComponentError: true };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        this.trackException(error, { stack: errorInfo.componentStack })
    }

    public componentDidMount() {
        window.addEventListener("error", this.handleError);
        window.addEventListener("unhandledrejection", this.handleUnhandledRejection);
    }

    public componentWillUnmount() {
        window.removeEventListener("error", this.handleError);
        window.removeEventListener("unhandledrejection", this.handleUnhandledRejection);
    }

    public render() {
        if (this.state.hasComponentError) {
            return <ErrorView />
        }

        return this.props.children;
    }

    private handleError = (ev: ErrorEvent) => {
        const { error, filename, lineno, colno, message } = ev;

        if (filename === "" && lineno === 0 && colno === 0 && message === "Script error.") {
            // "Script error." sind Fehler, die in Skripten von anderen origins
            // auftreten. Da wir da sowieso nichts machen können, ignorieren wir
            // diese.
            return;
        }

        this.trackException(
            error,
            {
                path: [
                    filename,
                    lineno.toString(),
                    colno.toString()
                ],
                message,
            });
    }

    private handleUnhandledRejection = (ev: PromiseRejectionEvent) => {
        if (!(ev.reason instanceof HttpRequestError)) {
            return;
        }

        ev.preventDefault();
        if (!ev.reason.response.bodyUsed) {
            this.props.snackbar.pushResponseErrors(
                ev.reason.requestNameOrUrl, ev.reason.response.clone());
        }
    }

    private trackException(error?: Error, customProperties?: CustomProperties) {
        appInsights.trackException(
            {
                exception: error,
                severityLevel: SeverityLevel.Error
            },
            customProperties);
    }
}

export default withSnackbar(GlobalErrorHandler);